import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import MyReportPage from "@/pages/MyReportPage";
import MyHistoryPage from "@/pages/MyHistoryPage";
import TeamFeedPage from "@/pages/TeamFeedPage";
import UsersPage from "@/pages/UsersPage";
import UserHistoryPage from "@/pages/UserHistoryPage";
import ActivityPage from "@/pages/ActivityPage";
import {
  LayoutDashboard, FileText, History, Users, UserCog, Activity,
  Menu, LogOut, FileEdit
} from "lucide-react";

const BASE_NAV = [
  { id: "dashboard", label: "Yönetim Özeti", icon: LayoutDashboard, group: "GENEL", roles: ["admin","employee"] },
  { id: "my-report", label: "Günlük Rapor", icon: FileEdit, group: "RAPOR", roles: ["admin","employee"] },
  { id: "my-history", label: "Rapor Geçmişim", icon: History, group: "RAPOR", roles: ["admin","employee"] },
  { id: "team-feed", label: "Ekip Raporları", icon: Users, group: "EKİP", roles: ["admin","employee"] },
  { id: "users", label: "Kullanıcılar", icon: UserCog, group: "YÖNETİM", roles: ["admin"] },
  { id: "activity", label: "Giriş Geçmişi", icon: Activity, group: "YÖNETİM", roles: ["admin"] },
];

function Shell() {
  const { user, logout, isAdmin } = useAuth();
  const [page, setPage] = useState("dashboard");
  const [mobileNav, setMobileNav] = useState(false);
  const [viewingUserId, setViewingUserId] = useState(null);

  const visibleNav = BASE_NAV.filter((n) => n.roles.includes(user.role));
  const groups = [...new Set(visibleNav.map((n) => n.group))];

  const openUserHistory = (u) => {
    setViewingUserId(u.id);
    setPage("user-history");
  };

  const renderPage = () => {
    if (page === "dashboard") return <DashboardPage />;
    if (page === "my-report") return <MyReportPage />;
    if (page === "my-history") return <MyHistoryPage />;
    if (page === "team-feed") return <TeamFeedPage />;
    if (page === "users") return <UsersPage onViewHistory={openUserHistory} />;
    if (page === "user-history" && viewingUserId) {
      return <UserHistoryPage userId={viewingUserId} onBack={() => { setViewingUserId(null); setPage("users"); }} />;
    }
    if (page === "activity") return <ActivityPage />;
    return <DashboardPage />;
  };

  return (
    <div className={`app-shell ${mobileNav ? "nav-open" : ""}`}>
      <aside className="sidebar" data-testid="sidebar">
        <div className="brand">
          <div className="brand-mark"><FileText size={18} strokeWidth={2.4}/></div>
          <div><strong>GÜNLÜK RAPOR</strong><span>EKİP TAKİBİ</span></div>
        </div>
        <p className="workspace-label">ÇALIŞMA ALANI</p>
        <nav className="nav-list">
          {groups.map((g) => (
            <div className="nav-group" key={g}>
              <p className="nav-group-label">{g}</p>
              {visibleNav.filter((n) => n.group === g).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  className={page === id ? "active" : ""}
                  onClick={() => { setPage(id); setMobileNav(false); }}
                  data-testid={`nav-${id}`}
                >
                  <Icon size={17}/><span>{label}</span>
                  {id === "dashboard" && <i className="nav-live"/>}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <div className="user-chip" data-testid="user-chip">
            <div className="avatar">{(user.name || "?").charAt(0)}</div>
            <div className="user-info">
              <strong>{user.name}</strong>
              <small>{user.position || (isAdmin ? "Yönetici" : "Çalışan")}</small>
            </div>
          </div>
          <button className="logout-btn" onClick={logout} data-testid="logout-button">
            <LogOut size={14}/> Çıkış yap
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="burger" onClick={() => setMobileNav((v) => !v)} data-testid="mobile-nav-toggle">
            <Menu size={18}/>
          </button>
          <div className="topbar-crumbs">
            <span className="crumb-logo"><FileText size={13}/> Günlük Rapor</span>
            <span className="crumb-sep">›</span>
            <span className="crumb-current">{visibleNav.find((n) => n.id === page)?.label || "Yönetim Özeti"}</span>
          </div>
          <div className="topbar-actions">
            <span className="top-date" data-testid="today-chip">
              {new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" })}
            </span>
          </div>
        </header>
        <section className="main-body">{renderPage()}</section>
      </main>
    </div>
  );
}

function AppInner() {
  const { user, loading } = useAuth();
  if (loading) return <div className="boot-loading">Yükleniyor…</div>;
  return (
    <>
      <Toaster richColors position="top-right"/>
      {user ? <Shell/> : <LoginPage/>}
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner/>
    </AuthProvider>
  );
}
